import React, { use, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { GridCellProps, GridFilterChangeEvent } from '@progress/kendo-react-grid';
import ErrorMessage from '@components-clean/core/error';
import createGridCustomCells, { INamedItem } from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell, EvaluationScoreCell, YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import Loading from '@components-clean/core/loading/loading';
import AddRiskDialog, {
    RiskFormInputs,
} from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/add-risk-dialog';
import UnassignRiskDialog from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/unassign-risk-dialog';
import { CtqPageRiskFields } from '@pages-clean/risk-assessment/critical-to-quality-factors/types';
import { CTQFactorsContext } from '@pages-clean/risk-assessment/interfaces/context';
import { PersistentStorageKeys } from '@utils-clean/persistent-storage-keys';
import {
    AuditApplicationArea,
    AuditDataType,
    AuditServiceType,
    CountryInputType,
    NamedItemType,
    RiskStatusType,
    TimeFrequencyType,
} from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useBoolean, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import {
    stringDateWithoutTimeFromCurrentUtcDate,
    stringDateWithoutTimeFromLocal,
    stringDateWithoutTimeFromUtc,
} from '@utils/convertor';
import { ShowError, ShowMessage, ShowSuccess } from '@utils/notification';
import {
    convertRiskScoreColorToWeight,
    mapRiskReviewStatus,
    mapRiskStatus,
    RiskReviewStatuses,
    RiskStatuses,
} from '@utils/risk-helper';
import { notEmpty } from '@utils/utility-helpers';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import { getNamedItemsByType } from '@queries/generated/common.generated';
import { CriticalVariableAssociatedRiskFields as RiskFields } from '@queries/generated/fragments.generated';
import {
    getCriticalVariableAssociatedRisks_OpraQueries,
    getCriticalVariableAssociatedRisksVariables,
    upsertRisk,
    upsertRiskVariables,
} from '@queries/generated/risk-assessment.generated';
import { CTQ_WIDGET_VALUES, GET_CRITICAL_VARIABLE_ASSOCIATED_RISKS, UPSERT_RISK } from '@queries/risk-assessment';

interface RisksGridProps {
    selectedRiskId: string | null;
}

const RisksGrid = ({ selectedRiskId }: RisksGridProps) => {
    const { t, ready } = useTranslation('ram', {
        keyPrefix: 'ctqf.ctqFactors.risksGrid',
    });
    const { t: tCommon } = useTranslation('common');
    const { current, setCurrent } = use(CTQFactorsContext);
    const exportFileName = `${current.selectionState.selectedItem?.description?.substring(0, 80)}_${t('exportFileName')}`;
    const { study } = current;
    const { TooltipCell, ActiveInactiveCell } = createGridCustomCells();
    const { loading, error, data, refetch } = useQuery<
        getCriticalVariableAssociatedRisks_OpraQueries,
        getCriticalVariableAssociatedRisksVariables
    >(GET_CRITICAL_VARIABLE_ASSOCIATED_RISKS, {
        variables: {
            studyId: study?.id,
            criticalVariableId: current.selectionState.selectedItem?.id,
        },
        skip: !current.selectionState.selectedItem?.id,
    });
    const { data: riskCategoryData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.RISK_CATEGORY },
    });
    const user = useAuthorization();
    const canEdit = user?.canWrite?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });

    const [flattenedCvRisks, setFlattenedCvRisks] = useState<CtqPageRiskFields[]>([]);
    const riskStatuses = ready ? RiskStatuses() : [];
    const [riskReviewStatuses, setRiskReviewStatuses] = useState<string[]>([]);
    const [selectedRisk, setSelectedRisk] = useState<CtqPageRiskFields>();
    const showUnassignRiskDialog = useBoolean(false);
    const showEditRiskDialog = useBoolean(false);
    const [filterState, setFilterState] = useState<CompositeFilterDescriptor | undefined>({
        logic: 'and',
        filters: [],
    });

    useEffect(() => {
        if (ready) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setRiskReviewStatuses(RiskReviewStatuses());
        }
    }, [ready]);

    const [upsertRiskMutation] = useMutation<upsertRisk, upsertRiskVariables>(UPSERT_RISK);

    const onEditRiskClose = () => {
        showEditRiskDialog.setFalse();
    };

    const onSubmitEditRisk = async (editRiskData: RiskFormInputs) => {
        upsertRiskMutation({
            variables: {
                risk: {
                    id: selectedRisk?.id,
                    studyId: study?.id,
                    libraryReferenceId: selectedRisk?.libraryReferenceId,
                    ownerId: editRiskData.owner?.id,
                    controlStrategyId: editRiskData.controlStrategy?.id,
                    categoryId: editRiskData.category?.id,
                    responsibleFunctionId: editRiskData.responsibleFunction?.id,
                    statement: editRiskData.statement,
                    // The user does not set this in the dialog (they set it in the Complete Review dialog instead)
                    // so the date always comes direct from the DB - treat it as UTC
                    dateLastReviewed: selectedRisk?.dateLastReviewed
                        ? stringDateWithoutTimeFromUtc(selectedRisk.dateLastReviewed)
                        : null,
                    dateIdentified: editRiskData.dateIdentified
                        ? stringDateWithoutTimeFromLocal(editRiskData.dateIdentified)
                        : null,
                    closedDate: editRiskData.closedDate
                        ? stringDateWithoutTimeFromLocal(editRiskData.closedDate)
                        : null,
                    status: RiskStatusType[editRiskData.status?.id as keyof typeof RiskStatusType],
                    recordId: selectedRisk?.recordId,
                    reviewRequired: editRiskData.reviewRequired,
                    reviewFrequencyPeriod: editRiskData.reviewRequired ? editRiskData.reviewFrequencyPeriod : null,
                    reviewFrequencyUnits: editRiskData.reviewRequired
                        ? TimeFrequencyType[editRiskData.reviewFrequencyUnit?.id as keyof typeof TimeFrequencyType]
                        : null,
                    controls: selectedRisk?.controlIds,
                    active: selectedRisk?.active,
                    internalOnly: editRiskData.internal,
                    vendorIds: editRiskData.vendors?.map((v) => v.id) ?? [],
                    countries: editRiskData.countries?.map((c) => c as CountryInputType) ?? [],
                    qualityIssueId: editRiskData.qualityIssue?.id,
                    qualityIssueReference: editRiskData.qualityIssueReference,
                },
            },
            refetchQueries: [
                {
                    query: CTQ_WIDGET_VALUES,
                    variables: { studyId: study?.id },
                },
            ],
        })
            .then(async (response) => {
                if (!response?.data?.upsertRisk) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                showEditRiskDialog.setFalse();
                await refetch();
                setSelectedRisk(selectedRisk);
                ShowSuccess(tCommon('actionsSuccessfulToast'));
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    useEffect(() => {
        if (data && data.associatedRisks) {
            if (!current.riskGridIsFiltered) {
                // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
                // eslint-disable-next-line @eslint-react/set-state-in-effect
                setFilterState({
                    logic: 'and',
                    filters: [],
                });
                setCurrent((prevState) => ({
                    ...prevState,
                    riskGridIsFiltered: false,
                }));
            }

            const array = data.associatedRisks.filter(notEmpty).map((item: RiskFields) => {
                return {
                    ...item,
                    createdBy: item.createdBy ? `${item.createdBy.firstName} ${item.createdBy.lastName}` : '',
                    updatedBy: item.updatedBy ? `${item.updatedBy.firstName} ${item.updatedBy.lastName}` : '',
                    owner: item.owner
                        ? {
                              firstName: item.owner.firstName,
                              lastName: item.owner.lastName,
                              name: item.owner.fullName,
                              active: item.owner.active,
                          }
                        : {
                              firstName: '',
                              lastName: '',
                              name: '',
                              active: true,
                          },
                    status: item.status ? mapRiskStatus(item.status) : '',
                    reviewStatus: item.reviewStatus ? mapRiskReviewStatus(item.reviewStatus) : '',
                };
            });

            // Sort by status
            let sortedResult = array.sort((a: CtqPageRiskFields, b: CtqPageRiskFields) => {
                if (a.status === b.status) return 0;
                if (
                    a.status !== mapRiskStatus(RiskStatusType.NOT_APPLICABLE) &&
                    a.status !== mapRiskStatus(RiskStatusType.CLOSED) &&
                    (b.status === mapRiskStatus(RiskStatusType.NOT_APPLICABLE) ||
                        b.status === mapRiskStatus(RiskStatusType.CLOSED))
                )
                    return -1;

                return 0;
            });

            // Sort by Color
            sortedResult = sortedResult.sort((a: CtqPageRiskFields, b: CtqPageRiskFields) => {
                if (
                    a.score?.colour === b.score?.colour ||
                    ((a.status === mapRiskStatus(RiskStatusType.NOT_APPLICABLE) ||
                        a.status === mapRiskStatus(RiskStatusType.CLOSED)) &&
                        b.status !== mapRiskStatus(RiskStatusType.NOT_APPLICABLE) &&
                        b.status !== mapRiskStatus(RiskStatusType.CLOSED))
                )
                    return 0;
                if (
                    a.score?.colour &&
                    b.score?.colour &&
                    convertRiskScoreColorToWeight(a.score?.colour) > convertRiskScoreColorToWeight(b.score?.colour)
                )
                    return -1;

                if (a.score?.colour && !b.score?.colour) return -1;
                return 1;
            });
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            /* eslint-disable @eslint-react/set-state-in-effect */
            setFlattenedCvRisks(sortedResult);
            if (selectedRiskId) {
                setSelectedRisk(sortedResult.find((risk: CtqPageRiskFields) => risk.id === selectedRiskId));
            }
            /* eslint-enable @eslint-react/set-state-in-effect */
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [data]);

    const filterChanged = async (event: GridFilterChangeEvent) => {
        const hasFiltersApplied = event.filter ? event.filter.filters.length > 0 : false;

        setCurrent((prevState) => ({
            ...prevState,
            riskGridIsFiltered: hasFiltersApplied,
        }));
    };

    const handleUnassignRisk = async (result: string) => {
        showUnassignRiskDialog.setFalse();
        if (result === 'success') {
            await refetch();
            ShowMessage({ text: tCommon('common:actionsSuccessfulToast'), type: 'success' });
        } else {
            ShowError(tCommon('common:errorMessage'));
        }
    };

    const columns: GridColumnProps[] = [
        {
            id: 'statement',
            field: 'statement',
            title: t('columns.statement'),
            lockedByDefault: true,
            minWidth: 300,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'score',
            field: 'score',
            title: t('columns.score'),
            minWidth: 80,
            filter: 'numeric',
            filterKey: 'score',
            cells: { data: (props: GridCellProps) => EvaluationScoreCell({ ...props, showBlankText: false }) },
            ragColumn: true,
            singleValueRagColumn: true,
            evaluationColumn: true,
            multiValueExportTitles: {
                score: t('exportColumns.score'),
                colour: t('exportColumns.colour'),
            },
        },
        {
            id: 'dateIdentified',
            field: 'dateIdentified',
            title: t('columns.dateIdentified'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 180,
            cellName: 'DateCell',
        },
        {
            id: 'category',
            field: 'category',
            title: t('columns.category'),
            minWidth: 150,
            filter: 'text',
            filterKey: 'name',
            lovData: riskCategoryData?.namedItemsByType
                ? riskCategoryData.namedItemsByType.map((item) => item?.name as string)
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'status',
            field: 'status',
            title: t('columns.riskStatus'),
            minWidth: 150,
            filter: 'text',
            lovData: riskStatuses,
        },
        {
            id: 'reviewStatus',
            field: 'reviewStatus',
            title: t('columns.reviewStatus'),
            minWidth: 150,
            filter: 'text',
            lovData: riskReviewStatuses,
        },

        {
            id: 'owner',
            field: 'owner',
            title: t('columns.owner'),
            minWidth: 150,
            filterKey: 'name',
            filter: 'text',
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'internalOnly',
            field: 'internalOnly',
            title: t('columns.internal'),
            cells: { data: YesNoCell },
            minWidth: 90,
            filter: 'boolean',
        },
        {
            id: 'activeControls',
            field: 'activeControlCount',
            title: t('columns.activeControls'),
            minWidth: 110,
            filter: 'numeric',
        },
        {
            id: 'createdBy',
            field: 'createdBy',
            title: t('columns.createdBy'),
            minWidth: 150,
            showByDefault: false,
        },
        {
            id: 'created',
            field: 'created',
            title: t('columns.created'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 130,
            cellName: 'DateCell',
            showByDefault: false,
        },
        {
            id: 'updatedBy',
            field: 'updatedBy',
            title: t('columns.updatedBy'),
            minWidth: 150,
            showByDefault: false,
        },
        {
            id: 'updated',
            field: 'updated',
            title: t('columns.updated'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 130,
            cellName: 'DateCell',
            showByDefault: false,
        },
        {
            minWidth: 200,
            id: 'id',
            field: 'recordId',
            showByDefault: false,
            title: 'ID',
        },
    ];

    if (error) return <ErrorMessage />;

    if (current.selectionState.selectedItem?.id && !data) return <Loading />;

    return (
        <>
            <Grid
                excludeHiddenRowMenuActions
                title={t('title')}
                maxHeight="auto"
                testDataId="ctqf-risks-grid"
                data={flattenedCvRisks}
                defaultSelectedItem={selectedRisk}
                columns={columns}
                loading={loading}
                useLoadingOverlay
                showAddIcon={false}
                scrollable="virtual"
                dataItemKey="id"
                filter={filterState}
                onFilterChange={filterChanged}
                clearFilter={async () => {
                    setCurrent((prevState) => ({
                        ...prevState,
                        riskGridIsFiltered: false,
                    }));
                    setFilterState({
                        logic: 'and',
                        filters: [],
                    });
                }}
                rowMenuActions={[
                    {
                        id: 1,
                        label: t('actions.edit'),
                        onClick: (dataItem: unknown) => {
                            const risk = dataItem as CtqPageRiskFields;
                            setSelectedRisk(risk);
                            showEditRiskDialog.setTrue();
                        },
                        disabled: !canEdit,
                        icon: ['far', 'pen-to-square'],
                    },
                    {
                        id: 2,
                        label: t('actions.unassign'),
                        onClick: (dataItem: unknown) => {
                            const risk = dataItem as CtqPageRiskFields;
                            setSelectedRisk(risk);
                            showUnassignRiskDialog.setTrue();
                        },
                        icon: ['fal', 'link-slash'] as IconProp,
                        disabled: !canEdit,
                    },
                ]}
                exportFilename={`${exportFileName}_${study?.studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate(
                    'DD-MMM-YYYY',
                )}`}
                service={AuditServiceType.CORE}
                area={AuditApplicationArea.RISK_ASSESSMENT}
                dataType={AuditDataType.EXCEL_EXPORT}
                persistentStorageKey={String(PersistentStorageKeys['ctQ-grid'])}
            />
            {showUnassignRiskDialog.value && selectedRisk && current.selectionState.selectedItem && (
                <UnassignRiskDialog
                    onClose={() => {
                        showUnassignRiskDialog.setFalse();
                    }}
                    onSubmit={handleUnassignRisk}
                    ctqId={current.selectionState.selectedItem?.id}
                    risk={selectedRisk}
                    risks={flattenedCvRisks}
                />
            )}
            {showEditRiskDialog.value && selectedRisk && (
                <AddRiskDialog
                    onClose={onEditRiskClose}
                    onSubmit={onSubmitEditRisk}
                    studyRef={study?.id}
                    risk={selectedRisk}
                />
            )}
        </>
    );
};

export default RisksGrid;
